import { useState, useCallback } from 'react'
import { toast } from 'src/components/Overlays/Toast/Toast'

export interface UploadFile {
  file: File
  status: 'pending' | 'uploading' | 'completed' | 'failed'
  error?: string
}

export interface BulkUploadDocumentProgress {
  isActive: boolean
  files: UploadFile[]
  completedCount: number
  failedCount: number
  totalCount: number
}

let globalUploadProgress: BulkUploadDocumentProgress = {
  isActive: false,
  files: [],
  completedCount: 0,
  failedCount: 0,
  totalCount: 0,
}

// Subscribers for upload progress updates
const subscribers: Set<(progress: BulkUploadDocumentProgress) => void> = new Set()

export const subscribe = (callback: (progress: BulkUploadDocumentProgress) => void) => {
  subscribers.add(callback)
  return () => subscribers.delete(callback)
}

const notifySubscribers = () => {
  subscribers.forEach((callback) => callback(globalUploadProgress))
}

export const useBulkUploadDocumentProgress = () => {
  const [progress, setProgress] = useState<BulkUploadDocumentProgress>(globalUploadProgress)

  const startBulkDocumentUpload = useCallback((files: File[]) => {
    globalUploadProgress = {
      isActive: true,
      files: files.map((file) => ({
        file,
        status: 'pending',
      })),
      completedCount: 0,
      failedCount: 0,
      totalCount: files.length,
    }
    setProgress(globalUploadProgress)
    notifySubscribers()
  }, [])

  const updateFileUploadProgress = useCallback(
    (fileIndex: number, status: 'uploading' | 'completed' | 'failed', error?: string) => {
      if (fileIndex < globalUploadProgress.files.length) {
        const file = globalUploadProgress.files[fileIndex]
        file.status = status
        if (error) file.error = error

        if (status === 'completed') {
          globalUploadProgress.completedCount += 1
        } else if (status === 'failed') {
          globalUploadProgress.failedCount += 1
        }

        setProgress({ ...globalUploadProgress })
        notifySubscribers()
      }
    },
    []
  )

  const completeBulkDocumentUpload = useCallback((showToast = true) => {
    const failedFiles = globalUploadProgress.files.filter((f) => f.status === 'failed')

    if (showToast) {
      if (failedFiles.length === 0) {
        toast(
          `Successfully uploaded ${globalUploadProgress.completedCount} file${globalUploadProgress.completedCount !== 1 ? 's' : ''}`,
          'success'
        )
      } else {
        const failureMessage = failedFiles.map((f) => `${f.file.name}: ${f.error}`).join('\n')
        toast(
          `Uploaded ${globalUploadProgress.completedCount} file${globalUploadProgress.completedCount !== 1 ? 's' : ''} with ${failedFiles.length} failure${failedFiles.length !== 1 ? 's' : ''}:\n${failureMessage}`,
          'error'
        )
      }
    }

    globalUploadProgress = {
      isActive: false,
      files: [],
      completedCount: 0,
      failedCount: 0,
      totalCount: 0,
    }
    setProgress(globalUploadProgress)
    notifySubscribers()
  }, [])

  return {
    progress,
    startBulkDocumentUpload,
    updateFileUploadProgress,
    completeBulkDocumentUpload,
  }
}
